import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, LoginCredentials, RegisterData } from '@/types'
import api from '@/services/api'
import router from '@/router'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const permissions = ref<string[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userName = computed(() => user.value?.name || '')
  const userEmail = computed(() => user.value?.email || '')
  const companyId = computed(() => user.value?.company_id || null)
  const company = computed(() => user.value?.company || null)

  // Actions
  function hasPermission(permission: string): boolean {
    return permissions.value.includes(permission)
  }

  function hasAnyPermission(permissionList: string[]): boolean {
    return permissionList.some(p => permissions.value.includes(p))
  }

  async function login(credentials: LoginCredentials): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/login', credentials)
      
      if (response.data.success) {
        token.value = response.data.token
        user.value = response.data.user
        permissions.value = response.data.permissions || []
        
        localStorage.setItem('auth_token', response.data.token)
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`
        
        return true
      } else {
        error.value = response.data.message || 'Login failed'
        return false
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'An error occurred during login'
      return false
    } finally {
      loading.value = false
    }
  }

  async function register(data: RegisterData): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/register', data)
      
      if (response.data.success) {
        return true
      } else {
        error.value = response.data.message || 'Registration failed'
        return false
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'An error occurred during registration'
      return false
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    loading.value = true
    
    try {
      await api.post('/logout')
    } catch {
      // Ignore logout errors
    } finally {
      // Clear local state
      token.value = null
      user.value = null
      permissions.value = []
      
      localStorage.removeItem('auth_token')
      delete api.defaults.headers.common['Authorization']
      
      loading.value = false
      router.push({ name: 'login' })
    }
  }

  async function fetchUser(): Promise<void> {
    if (!token.value) {
      throw new Error('No token available')
    }

    loading.value = true
    error.value = null
    
    try {
      api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
      const response = await api.get('/user')
      
      if (response.data.success) {
        user.value = response.data.user
        permissions.value = response.data.permissions || []
      } else {
        throw new Error('Failed to fetch user')
      }
    } catch (err: any) {
      token.value = null
      localStorage.removeItem('auth_token')
      delete api.defaults.headers.common['Authorization']
      throw err
    } finally {
      loading.value = false
    }
  }

  async function forgotPassword(email: string): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/password/email', { email })
      return response.data.success
    } catch (err: any) {
      error.value = err.response?.data?.message || 'An error occurred'
      return false
    } finally {
      loading.value = false
    }
  }

  async function resetPassword(data: { 
    token: string
    email: string
    password: string
    password_confirmation: string 
  }): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/password/reset', data)
      return response.data.success
    } catch (err: any) {
      error.value = err.response?.data?.message || 'An error occurred'
      return false
    } finally {
      loading.value = false
    }
  }

  async function changePassword(data: {
    current_password: string
    password: string
    password_confirmation: string
  }): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/change-password', data)
      return response.data.success
    } catch (err: any) {
      error.value = err.response?.data?.message || 'An error occurred'
      return false
    } finally {
      loading.value = false
    }
  }

  function clearError(): void {
    error.value = null
  }

  // Initialize auth state on store creation
  function initializeAuth(): void {
    const storedToken = localStorage.getItem('auth_token')
    if (storedToken) {
      token.value = storedToken
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`
    }
  }

  return {
    // State
    user,
    token,
    permissions,
    loading,
    error,
    
    // Getters
    isAuthenticated,
    userName,
    userEmail,
    companyId,
    company,
    
    // Actions
    hasPermission,
    hasAnyPermission,
    login,
    register,
    logout,
    fetchUser,
    forgotPassword,
    resetPassword,
    changePassword,
    clearError,
    initializeAuth
  }
}, {
  persist: {
    key: 'auth',
    storage: localStorage,
    pick: ['token', 'user', 'permissions']
  }
})
